import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { useTranslation } from 'react-i18next';
import { IconAlertOctagon, IconCheck } from '@tabler/icons-react';
import { imagesPath } from '@shared/css.const';
import { Image } from '@mantine/core';

const removeQueryParams = () => {
  const url = window.location.origin + window.location.pathname;
  window.history.replaceState({}, '', url);
};

type Props = {
  message: 'success' | 'cancel' | string;
  credits: number;
  isOpen: boolean;
  close: () => void;
  openBuyCreditsSlido: () => void;
};

export const PaymentCallBackSlido = (props: Props) => {
  const {
    isOpen, close, message, credits, openBuyCreditsSlido,
  } = props;

  const { t } = useTranslation();

  const isSuccess = message === 'success';

  const closeSlido = () => {
    close();
    removeQueryParams();
  };

  if (!['success', 'cancel'].includes(message)) {
    return <></>;
  }

  return (
    <BaseSlido
      opened={ isOpen }
      close={ closeSlido }
      title={ (
        <Flex direction='column' gap='2' p='6' pb='0'>
          <Text size='5xl'>
            {t('payments.buySlido.title')}
          </Text>
          <Text size='md' color='neutral.500'>
            {t('payments.buySlido.description')}
          </Text>
        </Flex>
      ) }
      footer={ (
        <Flex justify='end'>
          { isSuccess
            ? (
              <Button
                onClick={ closeSlido }
              >
                {t('payments.callback.done')}
              </Button>
            )
            : (
              <Flex gap={ 3 }>
                <Button
                  variant='secondary'
                  onClick={ closeSlido }
                >
                  {t('payments.callback.close')}
                </Button>
                <Button
                  onClick={ () => { closeSlido(); openBuyCreditsSlido(); } }
                >
                  {t('payments.callback.tryAgain')}
                </Button>
              </Flex>
            )}
        </Flex>
      ) }
    >
      <Box p={ 6 } h='97%'>
        <Flex direction='column' align='center' mt='spacing-24' gap='spacing-6'>
          <Flex
            borderRadius='full'
            backgroundColor={ isSuccess ? 'fg-success-primary_alt' : 'fg-warning-primary_alt' }
            w={ 120 }
            h={ 120 }
            justify='center'
            align='center'
          >
            { isSuccess
              ? <IconCheck size={ 48 } color='white' />
              : <IconAlertOctagon size={ 48 } color='white' />}
          </Flex>
          { isSuccess
            && (
              <Flex
                backgroundColor='utility-blue-50'
                borderRadius='radius-2'
                borderColor='utility-blue-200'
                borderWidth={ 1 }
                padding='4px 16px'
                gap={ 1 }
                alignItems='center'
              >
                <Image
                  src={ `${imagesPath}/credits.png` }
                  w={ 24 }
                />
                <Text size='md' weight='medium'>
                  {t('payments.callback.success.creditsAdded', { credits })}
                </Text>
              </Flex>
            )}

          <Text size='5xl' weight='semiBold'>
            {isSuccess
              ? t('payments.callback.success.title')
              : t('payments.callback.cancel.title')}
          </Text>

          { isSuccess
            ? (
              <Flex direction='column' gap='8px' align='center'>
                <Text size='xl' color='text-secondary' weight='regular'>
                  {t('payments.callback.success.descriptionFirstLine', { credits })}
                </Text>
                <Text size='xl' color='text-secondary' weight='regular'>
                  {t('payments.callback.success.descriptionSecondLine')}
                </Text>
                <Text size='xl' color='text-secondary' weight='regular'>
                  {t('payments.callback.success.descriptionThirdLine')}
                </Text>
              </Flex>
            )
            : (
              <Flex direction='column' gap='8px' align='center' maxW={ 552 }>
                <Text size='xl' color='text-secondary' weight='regular' textAlign='center'>
                  {t('payments.callback.cancel.description')}
                </Text>
              </Flex>
            )}
        </Flex>
      </Box>
    </BaseSlido>
  );
};
